import axios from "axios";
import { notification } from "antd";
import { REQUEST_STATUS } from "../utils/constants";
import { getTenant, isLoadingRoute } from "../components/common/utils/common";

const hostname = process.env.REACT_APP_API_HOST_NAME;

export const axiosSecured = axios.create({
  baseURL: `${hostname}/api`,
  headers: {
    "Content-Type": "application/json",
  },
});

// ---------- Auth helpers (injected once) ----------
let getToken = null;
let logoutFn = null;

export const injectAuth = (getAccessTokenSilently, logout) => {
  getToken = getAccessTokenSilently;
  logoutFn = logout;
};

// ---------- Request interceptor ----------
axiosSecured.interceptors.request.use(
  async (config) => {
    if (!config.headers.Authorization && getToken) {
      const token = await getToken();
      config.headers.Authorization = `Bearer ${token}`;
      config.headers["X-TenantId"] = getTenant();
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ---------- Response interceptor ----------
axiosSecured.interceptors.response.use(
  (response) => {
    if (response.data?.status === REQUEST_STATUS.error) {
      if (!isLoadingRoute()) {
        notification.error({
          message: "An Unexpected Error Occurred",
          description: response.data.message,
        });
      }

      return Promise.resolve(new Error(response.data.message));
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config || {};
    const status = error.response?.status;
    const data = error.response?.data;

    // ---------- Handle 400 ----------
    if (status === 400) {
      if (!isLoadingRoute()) {
        notification.error({
          message: `Error${data.title ? `: ${data.title}` : ""}`,
          placement: "bottomRight",
          description:
            data?.detail || "Invalid request. Please check your input.",
        });
      }

      return Promise.resolve({
        data: null,
        status,
        handled: true,
      });
    }

    // ---------- Retry once on 401 ----------
    if (status === 401 && !originalRequest.__retry && getToken) {
      originalRequest.__retry = true;

      try {
        const token = await getToken();
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axiosSecured(originalRequest);
      } catch {
        silentLogout();

        return Promise.resolve({
          data: null,
          status: 401,
          handled: true,
        });
      }
    }

    // ---------- Generic fallback ----------
    if (!isLoadingRoute()) {
      notification.error({
        message: "An Unexpected Error Occurred",
        placement: "bottomRight",
        description: data?.message || error.message,
      });
    }

    return Promise.resolve({
      data: null,
      status,
      handled: true,
    });
  }
);

// ---------- Silent logout ----------
const silentLogout = () => {
  if (logoutFn) {
    logoutFn({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  }
};
